// /products/web/src/utils/failure-taxonomy.ts
/**
 * Failure taxonomy and log-level mapping per ADR-03
 * (Standardize Logging Levels and Properties).
 */

export type FailureType =
  | "CallerError"
  | "BusinessRuleDenial"
  | "DependencyFailure"
  | "RetryableFailure"
  | "TerminalFailure"
  | "UnexpectedSystemFailure";

export type LogSeverity = "warn" | "error";

interface FailureClassification {
  level: LogSeverity;
  alertable: boolean;
}

/**
 * ADR-03 Section 3: Log Level Mapping
 */
export const FAILURE_TAXONOMY: Record<FailureType, FailureClassification> = {
  CallerError: { level: "warn", alertable: false },
  BusinessRuleDenial: { level: "warn", alertable: false },
  DependencyFailure: { level: "error", alertable: true },
  RetryableFailure: { level: "warn", alertable: false },
  TerminalFailure: { level: "error", alertable: true },
  UnexpectedSystemFailure: { level: "error", alertable: true },
};

export const VALID_FAILURE_TYPES: FailureType[] = Object.keys(FAILURE_TAXONOMY) as FailureType[];

/**
 * Classifies a non-ok HTTP response according to ADR-03.
 */
export const getFailureTypeForHttpStatus = (status: number): FailureType =>
  status >= 500 ? "DependencyFailure" : "CallerError";

/**
 * Resolves the ADR-03 mandated log level for a failure type.
 * Unclassified failures default to "error" (Unexpected System Failure).
 */
export const getLogLevelForFailureType = (errorType?: FailureType): LogSeverity =>
  errorType ? FAILURE_TAXONOMY[errorType].level : "error";

/**
 * Resolves whether a failure type is alertable per ADR-03.
 * Unclassified failures default to alertable (Unexpected System Failure).
 */
export const isAlertableFailureType = (errorType?: FailureType): boolean =>
  errorType ? FAILURE_TAXONOMY[errorType].alertable : true;

export const isValidFailureType = (value: unknown): value is FailureType =>
  typeof value === "string" && (VALID_FAILURE_TYPES as string[]).includes(value);
